import React, { useState, useEffect, useCallback } from "react";
import {
    FiPlus,
    FiSearch,
    FiUsers,
    FiUser,
    FiTrash2,
    FiCreditCard,
    FiShield,
} from "react-icons/fi";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import fineService from "../../services/fineService";
import studentService from "../../services/studentService";
import courseService from "../../services/courseService";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

const PRESETS = [
    { label: "👔 Uniform Fee", fineType: "Uniform Fee", category: "Charge", title: "Annual Uniform Kit & Blazers", defaultAmount: 2300 },
    { label: "📖 Lab Manual (D.Pharm)", fineType: "Lab Manual", category: "Charge", title: "D.Pharm Practical Lab Manuals Set", defaultAmount: 700 },
    { label: "📖 Lab Manual (B.Pharm)", fineType: "Lab Manual", category: "Charge", title: "B.Pharm Practical Lab Manuals Set", defaultAmount: 1200 },
    { label: "🧪 Lab Breakage Fine", fineType: "Lab Fine", category: "Fine", title: "Chemistry / Pharmaceutics Lab Glassware Damage", defaultAmount: 350 },
    { label: "📚 Library Fine", fineType: "Library Fine", category: "Fine", title: "Overdue Library Book Return Penalty", defaultAmount: 100 },
    { label: "💳 ID Card / Clearance", fineType: "Clearance / ID Card", category: "Charge", title: "Duplicate Student Identity Card / Badge", defaultAmount: 200 },
];

const FINE_TYPES = [
    "Uniform Fee",
    "Lab Manual",
    "Book Kit / Books",
    "Lab Fine",
    "Library Fine",
    "Discipline",
    "Late Fee",
    "Clearance / ID Card",
    "Activity / Sports",
    "Other",
];

export default function FinesManagement() {
    const { user, academicYear, academicYears, allColleges, selectedCollegeId: globalCollegeId, selectCollege, collegeData, collegeName } = useApp();
    const isMultiCollege = user?.role === "superadmin" || user?.role === "accountant";
    const isSuperAdmin = user?.role === "superadmin";

    const [fines, setFines] = useState([]);
    const [colleges, setColleges] = useState([]);
    const [selectedCollegeId, setSelectedCollegeId] = useState(() => {
        return globalCollegeId && globalCollegeId !== "all" ? globalCollegeId : "";
    });

    useEffect(() => {
        if (globalCollegeId !== undefined) {
            setSelectedCollegeId(globalCollegeId === "all" ? "" : globalCollegeId);
        }
    }, [globalCollegeId]);
    const [selectedAyId, setSelectedAyId] = useState(academicYear?._id || "ALL");
    const [stats, setStats] = useState({
        totalAmount: 0,
        pendingAmount: 0,
        paidAmount: 0,
        waivedAmount: 0,
        pendingCount: 0,
        paidCount: 0,
        waivedCount: 0,
    });
    const [loading, setLoading] = useState(true);

    // Filters
    const [activeTab, setActiveTab] = useState("ALL"); // "ALL" | "Charge" | "Fine" | "Pending" | "Paid" | "Waived"
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [typeFilter, setTypeFilter] = useState("ALL");
    const [courseFilter, setCourseFilter] = useState("ALL");
    const [yearFilter, setYearFilter] = useState("ALL");
    const [searchTerm, setSearchTerm] = useState("");
    const [courses, setCourses] = useState([]);

    // Modals
    const [showIssueModal, setShowIssueModal] = useState(false);
    const [issueMode, setIssueMode] = useState("single"); // "single" | "bulk"

    // Single student form
    const [singleForm, setSingleForm] = useState({
        student: "",
        category: "Charge",
        fineType: "Uniform Fee",
        title: "",
        amount: "",
        dueDate: "",
        remarks: "",
    });
    const [studentSearch, setStudentSearch] = useState("");
    const [studentsList, setStudentsList] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [searchingStudents, setSearchingStudents] = useState(false);

    // Bulk form
    const [bulkForm, setBulkForm] = useState({
        course: "",
        yearNumber: "",
        divisionName: "ALL",
        category: "Charge",
        fineType: "Uniform Fee",
        title: "",
        amount: "",
        dueDate: "",
        remarks: "",
    });
    const [bulkMatchCount, setBulkMatchCount] = useState(null);
    const [checkingBulkCount, setCheckingBulkCount] = useState(false);

    // Waive Modal
    const [showWaiveModal, setShowWaiveModal] = useState(false);
    const [fineToWaive, setFineToWaive] = useState(null);
    const [waiverReason, setWaiverReason] = useState("");
    const [submittingAction, setSubmittingAction] = useState(false);

    // Pay Modal
    const [showPayModal, setShowPayModal] = useState(false);
    const [fineToPay, setFineToPay] = useState(null);
    const [payForm, setPayForm] = useState({
        paymentMode: "Cash",
        receiptNo: "",
        transactionRef: "",
        paymentDate: new Date().toLocaleDateString("en-IN"),
    });

    // ── Load Colleges for Multi-College Users ─────────────────────────────
    useEffect(() => {
        if (allColleges && allColleges.length > 0) {
            setColleges(allColleges);
        } else if (isMultiCollege) {
            import("../../services/collegeService").then(({ default: collegeService }) => {
                collegeService.getAll()
                    .then((res) => {
                        const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
                        setColleges(list);
                    })
                    .catch(() => { });
            });
        }
    }, [isMultiCollege, allColleges]);

    // ── Load Courses ──────────────────────────────────────────────────────────
    useEffect(() => {
        courseService.getAll()
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
                setCourses(list);
            })
            .catch(() => { });
    }, []);

    // ── Tab Click Handler ─────────────────────────────────────────────────────
    const handleTabClick = (key) => {
        setActiveTab(key);
        if (key === "Pending" || key === "Paid" || key === "Waived") {
            setStatusFilter(key);
        } else if (key === "ALL") {
            setStatusFilter("ALL");
        }
    };

    // ── Fetch Fines ───────────────────────────────────────────────────────────
    const fetchFines = useCallback(async () => {
        try {
            setLoading(true);
            let queryCategory = undefined;
            if (activeTab === "Charge") queryCategory = "Charge";
            else if (activeTab === "Fine") queryCategory = "Fine";

            let queryStatus = undefined;
            if (activeTab === "Pending" || activeTab === "Paid" || activeTab === "Waived") {
                queryStatus = activeTab;
            } else if (statusFilter !== "ALL") {
                queryStatus = statusFilter;
            }

            const effectiveCollege = selectedCollegeId || (globalCollegeId !== "all" ? globalCollegeId : undefined);
            const params = {
                academicYear: selectedAyId !== "ALL" ? selectedAyId : undefined,
                college: effectiveCollege || undefined,
                status: queryStatus,
                category: queryCategory,
                fineType: typeFilter !== "ALL" ? typeFilter : undefined,
                course: courseFilter !== "ALL" ? courseFilter : undefined,
                yearNumber: yearFilter !== "ALL" ? yearFilter : undefined,
                search: searchTerm.trim() || undefined,
                limit: 200,
            };
            const res = await fineService.getAll(params);
            const list = Array.isArray(res.data)
                ? res.data
                : Array.isArray(res.data?.data)
                    ? res.data.data
                    : Array.isArray(res)
                        ? res
                        : [];
            setFines(list);
            setStats(res.stats || res.data?.stats || {
                totalAmount: 0,
                pendingAmount: 0,
                paidAmount: 0,
                waivedAmount: 0,
                pendingCount: 0,
                paidCount: 0,
                waivedCount: 0,
            });
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to load records");
        } finally {
            setLoading(false);
        }
    }, [selectedAyId, selectedCollegeId, activeTab, statusFilter, typeFilter, courseFilter, yearFilter, searchTerm]);

    useEffect(() => {
        fetchFines();
    }, [fetchFines]);

    // ── Apply Preset ──────────────────────────────────────────────────────────
    const applyPreset = (preset, mode) => {
        if (mode === "single") {
            setSingleForm((prev) => ({
                ...prev,
                category: preset.category,
                fineType: preset.fineType,
                title: preset.title,
                amount: preset.defaultAmount,
            }));
        } else {
            setBulkForm((prev) => ({
                ...prev,
                category: preset.category,
                fineType: preset.fineType,
                title: preset.title,
                amount: preset.defaultAmount,
            }));
        }
    };

    // ── Student Search for Single Issue ───────────────────────────────────────
    useEffect(() => {
        if (!studentSearch || !studentSearch.trim()) {
            setStudentsList([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                setSearchingStudents(true);
                const res = await studentService.getAll({
                    search: studentSearch.trim(),
                    status: "Active",
                    limit: 20,
                });
                const list = Array.isArray(res.data)
                    ? res.data
                    : Array.isArray(res.data?.students)
                        ? res.data.students
                        : (Array.isArray(res.data?.data) ? res.data.data : []);
                setStudentsList(list);
            } catch (e) {
                setStudentsList([]);
            } finally {
                setSearchingStudents(false);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [studentSearch]);

    // ── Bulk Target Count Checker ─────────────────────────────────────────────
    useEffect(() => {
        if (issueMode !== "bulk") return;
        const timer = setTimeout(async () => {
            try {
                setCheckingBulkCount(true);
                const params = {
                    academicYear: academicYear?._id,
                    status: "Active",
                    course: bulkForm.course || undefined,
                    yearNumber: bulkForm.yearNumber || undefined,
                    divisionName: bulkForm.divisionName !== "ALL" ? bulkForm.divisionName : undefined,
                    limit: 500,
                };
                const res = await studentService.getAll(params);
                const list = Array.isArray(res.data)
                    ? res.data
                    : Array.isArray(res.data?.students)
                        ? res.data.students
                        : (Array.isArray(res.data?.data) ? res.data.data : []);
                setBulkMatchCount(list.length);
            } catch (e) {
                setBulkMatchCount(0);
            } finally {
                setCheckingBulkCount(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [issueMode, bulkForm.course, bulkForm.yearNumber, bulkForm.divisionName, academicYear]);

    // ── Handle Single Issue ───────────────────────────────────────────────────
    const handleSingleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedStudent) {
            toast.error("Please search and select a student");
            return;
        }
        if (!singleForm.title || !singleForm.amount) {
            toast.error("Title and Amount are required");
            return;
        }

        try {
            setSubmittingAction(true);
            await fineService.issueSingle({
                student: selectedStudent._id,
                academicYear: academicYear?._id,
                category: singleForm.category,
                fineType: singleForm.fineType,
                title: singleForm.title,
                amount: Number(singleForm.amount),
                dueDate: singleForm.dueDate || undefined,
                remarks: singleForm.remarks,
            });
            toast.success(`${singleForm.category === "Charge" ? "Charge" : "Fine"} issued to ${selectedStudent.fullName}`);
            setShowIssueModal(false);
            setSelectedStudent(null);
            setStudentSearch("");
            setSingleForm({
                student: "",
                category: "Charge",
                fineType: "Uniform Fee",
                title: "",
                amount: "",
                dueDate: "",
                remarks: "",
            });
            fetchFines();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to issue item");
        } finally {
            setSubmittingAction(false);
        }
    };

    // ── Handle Bulk Issue ─────────────────────────────────────────────────────
    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        if (!bulkForm.title || !bulkForm.amount) {
            toast.error("Title and Amount per student are required");
            return;
        }
        if (!bulkMatchCount || bulkMatchCount === 0) {
            toast.error("No active students match the selected filter criteria");
            return;
        }

        const itemKind = bulkForm.category === "Charge" ? "charge" : "fine";
        if (!window.confirm(`Issue ${itemKind} "${bulkForm.title}" of ${inr(bulkForm.amount)} each to all ${bulkMatchCount} students?`)) {
            return;
        }

        try {
            setSubmittingAction(true);
            const res = await fineService.issueBulk({
                academicYear: academicYear?._id,
                course: bulkForm.course || undefined,
                yearNumber: bulkForm.yearNumber ? Number(bulkForm.yearNumber) : undefined,
                divisionName: bulkForm.divisionName,
                category: bulkForm.category,
                fineType: bulkForm.fineType,
                title: bulkForm.title,
                amount: Number(bulkForm.amount),
                dueDate: bulkForm.dueDate || undefined,
                remarks: bulkForm.remarks,
            });
            toast.success(res.data?.message || `Issued to ${bulkMatchCount} students`);
            setShowIssueModal(false);
            setBulkForm({
                course: "",
                yearNumber: "",
                divisionName: "ALL",
                category: "Charge",
                fineType: "Uniform Fee",
                title: "",
                amount: "",
                dueDate: "",
                remarks: "",
            });
            fetchFines();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to issue bulk item");
        } finally {
            setSubmittingAction(false);
        }
    };

    // ── Handle Waive ──────────────────────────────────────────────────────────
    const handleWaiveSubmit = async (e) => {
        e.preventDefault();
        if (!waiverReason.trim()) {
            toast.error("Please enter justification/reason for waiving");
            return;
        }
        try {
            setSubmittingAction(true);
            await fineService.waiveFine(fineToWaive._id, waiverReason);
            toast.success("Item has been waived successfully");
            setShowWaiveModal(false);
            setFineToWaive(null);
            setWaiverReason("");
            fetchFines();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to waive item");
        } finally {
            setSubmittingAction(false);
        }
    };

    // ── Handle Pay ────────────────────────────────────────────────────────────
    const handlePaySubmit = async (e) => {
        e.preventDefault();
        try {
            setSubmittingAction(true);
            const prefix = fineToPay.category === "Charge" ? "CHG" : "FINE";
            await fineService.payFine(fineToPay._id, {
                paymentMode: payForm.paymentMode,
                receiptNo: payForm.receiptNo || `${prefix}-${Date.now().toString().slice(-6)}`,
                paymentDate: payForm.paymentDate || new Date().toLocaleDateString("en-IN"),
                transactionRef: payForm.transactionRef,
            });
            toast.success("Payment recorded and marked as Settled");
            setShowPayModal(false);
            setFineToPay(null);
            setPayForm({ paymentMode: "Cash", receiptNo: "", transactionRef: "", paymentDate: new Date().toLocaleDateString("en-IN") });
            fetchFines();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to settle item");
        } finally {
            setSubmittingAction(false);
        }
    };

    // ── Handle Delete ─────────────────────────────────────────────────────────
    const handleDelete = async (fine) => {
        if (!window.confirm(`Delete record "${fine.title}" for ${fine.student?.fullName}?`)) return;
        try {
            await fineService.delete(fine._id);
            toast.success("Record deleted successfully");
            fetchFines();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to delete record");
        }
    };

    return (
        <div className="page-wrapper" style={{ padding: "1.5rem" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                        <h1 style={{ fontSize: "1.5rem", fontWeight: 800, margin: 0, color: "var(--text)" }}>
                            Student Charges &amp; Fines Management 👔 ⚖
                        </h1>
                        <span style={{
                            fontSize: 12,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 20,
                            background: !selectedCollegeId || selectedCollegeId === "all" ? "#f1f5f9" : "#e0f2fe",
                            color: !selectedCollegeId || selectedCollegeId === "all" ? "#475569" : "#0369a1",
                            border: "1px solid " + (!selectedCollegeId || selectedCollegeId === "all" ? "#cbd5e1" : "#bae6fd"),
                        }}>
                            {!selectedCollegeId || selectedCollegeId === "all" ? "🌐 All Campuses" : `🏢 ${collegeData?.name || collegeName}`}
                        </span>
                    </div>
                    <p style={{ color: "var(--text-muted)", margin: "0.25rem 0 0", fontSize: "0.875rem" }}>
                        Issue, track, collect, and waive one-time Institutional Charges (Uniform, Lab Manual, Books) and Fines (Lab Breakage, Library, Discipline). Maintained strictly independent of Tuition Fees.
                    </p>
                </div>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                        onClick={() => { setIssueMode("single"); setShowIssueModal(true); }}
                        className="btn btn-primary"
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}
                    >
                        <FiPlus size={16} /> Issue to Student
                    </button>
                    <button
                        onClick={() => { setIssueMode("bulk"); setShowIssueModal(true); }}
                        className="btn btn-outline"
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontWeight: 700, borderColor: "#003b67", color: "#003b67" }}
                    >
                        <FiUsers size={16} /> Bulk Charge Class
                    </button>
                </div>
            </div>

            {/* Statistics Cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
                <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "4px solid #3b82f6" }}>
                    <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
                        Total Charges &amp; Fines Issued
                    </div>
                    <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#1e293b", marginTop: "0.25rem" }}>
                        {inr(stats.totalAmount)}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.2rem" }}>
                        {fines.length} total records in view
                    </div>
                </div>

                <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "4px solid #ef4444" }}>
                    <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", color: "#dc2626" }}>
                        Pending Dues (Uncollected)
                    </div>
                    <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#dc2626", marginTop: "0.25rem" }}>
                        {inr(stats.pendingAmount)}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#dc2626", marginTop: "0.2rem" }}>
                        {stats.pendingCount || 0} unpaid records
                    </div>
                </div>

                <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "4px solid #10b981" }}>
                    <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", color: "#059669" }}>
                        Collected &amp; Settled
                    </div>
                    <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#059669", marginTop: "0.25rem" }}>
                        {inr(stats.paidAmount)}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#059669", marginTop: "0.2rem" }}>
                        {stats.paidCount || 0} payments collected
                    </div>
                </div>

                <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "4px solid #8b5cf6" }}>
                    <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", color: "#7c3aed" }}>
                        Waived / Excused
                    </div>
                    <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#7c3aed", marginTop: "0.25rem" }}>
                        {inr(stats.waivedAmount)}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#7c3aed", marginTop: "0.2rem" }}>
                        {stats.waivedCount || 0} items waived
                    </div>
                </div>
            </div>

            {/* Filter Ribbon & Category Tabs */}
            <div className="card" style={{ padding: "0.85rem 1rem", marginBottom: "1.25rem" }}>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", borderBottom: "1px solid var(--border)", paddingBottom: "0.75rem", marginBottom: "0.75rem" }}>
                    {[
                        { key: "ALL", label: `🌟 All Records (${stats.pendingCount + stats.paidCount + stats.waivedCount || fines.length})` },
                        { key: "Charge", label: "👔 Institutional Charges (Uniform, Lab Manual, Books)" },
                        { key: "Fine", label: "⚖ Fines & Penalties (Lab Breakage, Library, Discipline)" },
                        { key: "Pending", label: `⏳ Pending Dues (${stats.pendingCount || 0})` },
                        { key: "Paid", label: `✅ Settled / Paid (${stats.paidCount || 0})` },
                        { key: "Waived", label: `🛡 Waived (${stats.waivedCount || 0})` },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => handleTabClick(tab.key)}
                            style={{
                                padding: "6px 14px",
                                borderRadius: "6px",
                                border: activeTab === tab.key ? "1px solid #003b67" : "1px solid #e2e8f0",
                                background: activeTab === tab.key ? "#003b67" : "#f8fafc",
                                color: activeTab === tab.key ? "#fff" : "#334155",
                                fontWeight: activeTab === tab.key ? 700 : 500,
                                fontSize: "12.5px",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
                    {/* Search */}
                    <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
                        <FiSearch style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                        <input
                            type="text"
                            placeholder="Search student, enrollment, voucher or title..."
                            className="form-control"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ paddingLeft: "32px", fontSize: "12.5px" }}
                        />
                    </div>

                    {/* Multi-College Filter (SuperAdmin & Accountant) */}
                    {isMultiCollege && colleges.length > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                            <span style={{ fontSize: "11.5px", color: "var(--text-muted)", fontWeight: 600 }}>Campus:</span>
                            <select
                                className="form-control"
                                value={selectedCollegeId}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSelectedCollegeId(val);
                                    selectCollege(val || "all");
                                }}
                                style={{ fontSize: "12.5px", padding: "4px 8px", minWidth: "160px", fontWeight: 600 }}
                            >
                                <option value="">🌐 All Campuses</option>
                                {colleges.map((c) => (
                                    <option key={c._id || c.id} value={c._id || c.id}>
                                        {c.code ? `[${c.code}] ` : ""}{c.name || c.collegeName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Academic Session */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <span style={{ fontSize: "11.5px", color: "var(--text-muted)", fontWeight: 600 }}>Session:</span>
                        <select
                            className="form-control"
                            value={selectedAyId}
                            onChange={(e) => setSelectedAyId(e.target.value)}
                            style={{ fontSize: "12.5px", padding: "4px 8px" }}
                        >
                            <option value="ALL">All Academic Years</option>
                            {academicYears.map((ay) => (
                                <option key={ay._id} value={ay._id}>{ay.label}{ay.isCurrent ? " (Current)" : ""}</option>
                            ))}
                        </select>
                    </div>

                    {/* Course Filter */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <span style={{ fontSize: "11.5px", color: "var(--text-muted)", fontWeight: 600 }}>Course:</span>
                        <select
                            className="form-control"
                            value={courseFilter}
                            onChange={(e) => setCourseFilter(e.target.value)}
                            style={{ fontSize: "12.5px", padding: "4px 8px" }}
                        >
                            <option value="ALL">All Courses</option>
                            {courses.map((c) => (
                                <option key={c._id} value={c.name}>{c.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Year Filter */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <span style={{ fontSize: "11.5px", color: "var(--text-muted)", fontWeight: 600 }}>Year:</span>
                        <select
                            className="form-control"
                            value={yearFilter}
                            onChange={(e) => setYearFilter(e.target.value)}
                            style={{ fontSize: "12.5px", padding: "4px 8px" }}
                        >
                            <option value="ALL">All Years</option>
                            <option value="1">1st Year</option>
                            <option value="2">2nd Year</option>
                            <option value="3">3rd Year</option>
                            <option value="4">4th Year</option>
                        </select>
                    </div>

                    {/* Type Filter */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <span style={{ fontSize: "11.5px", color: "var(--text-muted)", fontWeight: 600 }}>Head:</span>
                        <select
                            className="form-control"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            style={{ fontSize: "12.5px", padding: "4px 8px" }}
                        >
                            <option value="ALL">All Heads</option>
                            {FINE_TYPES.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <span style={{ fontSize: "11.5px", color: "var(--text-muted)", fontWeight: 600 }}>Status:</span>
                        <select
                            className="form-control"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); if (activeTab === "Pending" || activeTab === "Paid" || activeTab === "Waived") setActiveTab(e.target.value); }}
                            style={{ fontSize: "12.5px", padding: "4px 8px" }}
                        >
                            <option value="ALL">All Status</option>
                            <option value="Pending">Pending (Unpaid)</option>
                            <option value="Paid">Paid</option>
                            <option value="Waived">Waived</option>
                        </select>
                    </div>

                    {(searchTerm || statusFilter !== "ALL" || typeFilter !== "ALL" || courseFilter !== "ALL" || yearFilter !== "ALL" || activeTab !== "ALL") && (
                        <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => {
                                setSearchTerm("");
                                setStatusFilter("ALL");
                                setTypeFilter("ALL");
                                setCourseFilter("ALL");
                                setYearFilter("ALL");
                                setActiveTab("ALL");
                            }}
                            style={{ fontSize: "11.5px", padding: "4px 8px", color: "#64748b" }}
                        >
                            Reset
                        </button>
                    )}
                </div>
            </div>

            {/* Fines Table */}
            <div className="card" style={{ overflow: "hidden" }}>
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: "40px", textAlign: "center" }}>#</th>
                                <th>Student Details</th>
                                <th>Category &amp; Title</th>
                                <th style={{ textAlign: "right" }}>Amount</th>
                                <th style={{ textAlign: "center" }}>Date Issued</th>
                                <th style={{ textAlign: "center" }}>Status</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>
                                        Loading records...
                                    </td>
                                </tr>
                            ) : fines.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>
                                        No records found matching the criteria.
                                    </td>
                                </tr>
                            ) : (
                                fines.map((fine, idx) => (
                                    <tr key={fine._id}>
                                        <td style={{ textAlign: "center", color: "var(--text-muted)" }}>{idx + 1}</td>
                                        <td>
                                            <div style={{ fontWeight: 700, color: "var(--text)" }}>
                                                {fine.student?.fullName || "—"}
                                            </div>
                                            <div style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
                                                {fine.student?.enrollmentNo ? `Reg: ${fine.student.enrollmentNo} · ` : ""}
                                                Yr {fine.student?.yearNumber || "1"} {fine.student?.divisionName ? `(Div ${fine.student.divisionName})` : ""}
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                                                <span style={{
                                                    fontSize: "11px",
                                                    fontWeight: 700,
                                                    padding: "2px 6px",
                                                    borderRadius: "4px",
                                                    background: fine.category === "Charge" ? "#e8f1f8" : "#fef2f2",
                                                    color: fine.category === "Charge" ? "#002747" : "#b91c1c",
                                                    border: `1px solid ${fine.category === "Charge" ? "#b6d5ec" : "#fecaca"}`,
                                                }}>
                                                    {fine.category === "Charge" ? "👔 CHARGE" : "⚖ FINE"}
                                                </span>
                                                <span style={{
                                                    fontSize: "11px",
                                                    fontWeight: 600,
                                                    padding: "1px 6px",
                                                    borderRadius: "4px",
                                                    background: "#f1f5f9",
                                                    color: "var(--text-muted)",
                                                }}>
                                                    {fine.fineType}
                                                </span>
                                                <strong style={{ fontSize: "13px" }}>{fine.title}</strong>
                                            </div>
                                            {fine.remarks && (
                                                <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>
                                                    {fine.remarks}
                                                </div>
                                            )}
                                            {fine.status === "Waived" && fine.waiverReason && (
                                                <div style={{ fontSize: "11px", color: "#7c3aed", fontStyle: "italic", marginTop: "2px" }}>
                                                    Waived: "{fine.waiverReason}"
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ textAlign: "right", fontWeight: 800, fontSize: "13.5px", color: fine.status === "Paid" ? "#16a34a" : fine.status === "Waived" ? "#64748b" : "#dc2626" }}>
                                            {inr(fine.amount)}
                                        </td>
                                        <td style={{ textAlign: "center", fontSize: "12px", color: "var(--text-muted)" }}>
                                            {fine.issuedDate ? new Date(fine.issuedDate).toLocaleDateString("en-IN") : "—"}
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                            {fine.status === "Paid" ? (
                                                <span style={{ background: "#dcfce7", color: "#16a34a", padding: "3px 8px", borderRadius: 99, fontSize: "11px", fontWeight: 700 }}>
                                                    ✓ Paid
                                                </span>
                                            ) : fine.status === "Waived" ? (
                                                <span style={{ background: "#ede9fe", color: "#7c3aed", padding: "3px 8px", borderRadius: 99, fontSize: "11px", fontWeight: 700 }}>
                                                    ⚖ Waived
                                                </span>
                                            ) : (
                                                <span style={{ background: "#fee2e2", color: "#dc2626", padding: "3px 8px", borderRadius: 99, fontSize: "11px", fontWeight: 700 }}>
                                                    ⌛ Pending
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            <div style={{ display: "inline-flex", gap: "0.35rem", alignItems: "center" }}>
                                                {fine.status === "Pending" && (
                                                    <>
                                                        <button
                                                            onClick={() => { setFineToPay(fine); setShowPayModal(true); }}
                                                            className="btn btn-sm btn-success"
                                                            style={{ padding: "3px 8px", fontSize: "11.5px", display: "inline-flex", alignItems: "center", gap: "3px" }}
                                                            title="Collect / Mark as Paid"
                                                        >
                                                            <FiCreditCard size={12} /> Pay
                                                        </button>
                                                        <button
                                                            onClick={() => { setFineToWaive(fine); setShowWaiveModal(true); }}
                                                            className="btn btn-sm btn-outline"
                                                            style={{ padding: "3px 8px", fontSize: "11.5px", color: "#7c3aed", borderColor: "#c4b5fd" }}
                                                            title="Waive Item"
                                                        >
                                                            <FiShield size={12} /> Waive
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(fine)}
                                                            className="btn btn-sm btn-outline"
                                                            style={{ padding: "3px 6px", color: "#dc2626", borderColor: "#fecaca" }}
                                                            title="Delete Record"
                                                        >
                                                            <FiTrash2 size={12} />
                                                        </button>
                                                    </>
                                                )}
                                                {fine.status === "Paid" && (
                                                    <span style={{ fontSize: "11px", color: "#16a34a", fontWeight: 600 }}>
                                                        {fine.paymentDetails?.paymentMode || "Paid"} · {fine.paymentDetails?.receiptNo || "Settled"}
                                                    </span>
                                                )}
                                                {fine.status === "Waived" && (
                                                    <span style={{ fontSize: "11px", color: "#7c3aed", fontWeight: 600 }}>
                                                        Excused
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── ISSUE MODAL (Single + Bulk) ─────────────────────────────── */}
            {showIssueModal && (
                <div className="modal-overlay" style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
                }}>
                    <div className="card" style={{ width: "100%", maxWidth: "580px", maxHeight: "90vh", overflowY: "auto" }}>
                        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800 }}>
                                {issueMode === "single" ? "Issue Charge / Fine to Student" : "Bulk Charge Entire Course / Class"}
                            </h3>
                            <button onClick={() => setShowIssueModal(false)} className="btn-close" style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}>✕</button>
                        </div>

                        <div style={{ padding: "1.25rem" }}>
                            {/* Mode Toggle Switch */}
                            <div style={{
                                display: "flex",
                                background: "#f1f5f9",
                                borderRadius: "8px",
                                padding: "4px",
                                marginBottom: "1rem",
                            }}>
                                <button
                                    type="button"
                                    onClick={() => setIssueMode("single")}
                                    style={{
                                        flex: 1,
                                        padding: "8px",
                                        borderRadius: "6px",
                                        border: "none",
                                        background: issueMode === "single" ? "#fff" : "transparent",
                                        fontWeight: issueMode === "single" ? 700 : 500,
                                        color: issueMode === "single" ? "#003b67" : "var(--text-muted)",
                                        boxShadow: issueMode === "single" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "0.4rem",
                                    }}
                                >
                                    <FiUser size={14} /> Single Student
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIssueMode("bulk")}
                                    style={{
                                        flex: 1,
                                        padding: "8px",
                                        borderRadius: "6px",
                                        border: "none",
                                        background: issueMode === "bulk" ? "#fff" : "transparent",
                                        fontWeight: issueMode === "bulk" ? 700 : 500,
                                        color: issueMode === "bulk" ? "#003b67" : "var(--text-muted)",
                                        boxShadow: issueMode === "bulk" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "0.4rem",
                                    }}
                                >
                                    <FiUsers size={14} /> Bulk / Class-wide
                                </button>
                            </div>

                            {/* Quick Presets Ribbon */}
                            <div style={{ marginBottom: "1.25rem" }}>
                                <label style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", display: "block", marginBottom: "6px" }}>
                                    ⚡ Quick Standard Charge / Fine Presets:
                                </label>
                                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                                    {PRESETS.map((p, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => applyPreset(p, issueMode)}
                                            style={{
                                                fontSize: "11.5px",
                                                padding: "4px 8px",
                                                borderRadius: "6px",
                                                border: "1px solid var(--border)",
                                                background: "#fff",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                fontWeight: 600,
                                            }}
                                        >
                                            <span>{p.label}</span>
                                            <span style={{ color: "#16a34a" }}>({inr(p.defaultAmount)})</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* ── Single Student Form ── */}
                            {issueMode === "single" && (
                                <form onSubmit={handleSingleSubmit}>
                                    <div className="form-group" style={{ marginBottom: "1rem" }}>
                                        <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                            Search Student *
                                        </label>
                                        {selectedStudent ? (
                                            <div style={{
                                                padding: "8px 12px",
                                                background: "#e8f1f8",
                                                border: "1px solid #b6d5ec",
                                                borderRadius: "6px",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}>
                                                <div>
                                                    <strong>{selectedStudent.fullName}</strong>
                                                    <span style={{ fontSize: "12px", color: "var(--text-muted)", marginLeft: "8px" }}>
                                                        {selectedStudent.enrollmentNo ? `Reg: ${selectedStudent.enrollmentNo}` : ""}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => { setSelectedStudent(null); setStudentSearch(""); }}
                                                    style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontWeight: 700 }}
                                                >
                                                    Change
                                                </button>
                                            </div>
                                        ) : (
                                            <div style={{ position: "relative" }}>
                                                <input
                                                    type="text"
                                                    placeholder="Type student name or reg number..."
                                                    className="form-control"
                                                    value={studentSearch}
                                                    onChange={(e) => setStudentSearch(e.target.value)}
                                                />
                                                {searchingStudents && <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "3px" }}>Searching...</div>}
                                                {studentsList.length > 0 && (
                                                    <div style={{
                                                        position: "absolute", top: "100%", left: 0, right: 0,
                                                        background: "#fff", border: "1px solid var(--border)",
                                                        borderRadius: "6px", maxHeight: "160px", overflowY: "auto",
                                                        zIndex: 10, boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                                                    }}>
                                                        {studentsList.map((s) => (
                                                            <div
                                                                key={s._id}
                                                                onClick={() => { setSelectedStudent(s); setStudentsList([]); }}
                                                                style={{ padding: "8px 12px", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                                                                className="hover:bg-slate-50"
                                                            >
                                                                <div style={{ fontWeight: 600, fontSize: "13px" }}>{s.fullName}</div>
                                                                <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                                                                    {s.enrollmentNo ? `Reg: ${s.enrollmentNo} · ` : ""} Yr {s.yearNumber}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                        <div>
                                            <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                                Category *
                                            </label>
                                            <select
                                                className="form-control"
                                                value={singleForm.category}
                                                onChange={(e) => setSingleForm({ ...singleForm, category: e.target.value })}
                                            >
                                                <option value="Charge">👔 Institutional Charge (Uniform, Lab Manual, Books)</option>
                                                <option value="Fine">⚖ Fine &amp; Penalty (Lab Breakage, Library, Discipline)</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                                Charge / Fine Type *
                                            </label>
                                            <select
                                                className="form-control"
                                                value={singleForm.fineType}
                                                onChange={(e) => setSingleForm({ ...singleForm, fineType: e.target.value })}
                                            >
                                                {FINE_TYPES.map((t) => (
                                                    <option key={t} value={t}>{t}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group" style={{ marginBottom: "1rem" }}>
                                        <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                            Title / Specific Description *
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Uniform Set & Blazer 2025-26, Broken Burette Fine"
                                            className="form-control"
                                            value={singleForm.title}
                                            onChange={(e) => setSingleForm({ ...singleForm, title: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                        <div>
                                            <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                                Amount (₹) *
                                            </label>
                                            <input
                                                type="number"
                                                placeholder="Amount in INR"
                                                className="form-control"
                                                value={singleForm.amount}
                                                onChange={(e) => setSingleForm({ ...singleForm, amount: e.target.value })}
                                                min="1"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                                Due Date (Optional)
                                            </label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={singleForm.dueDate}
                                                onChange={(e) => setSingleForm({ ...singleForm, dueDate: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                                        <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                            Remarks / Note
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Optional remarks"
                                            className="form-control"
                                            value={singleForm.remarks}
                                            onChange={(e) => setSingleForm({ ...singleForm, remarks: e.target.value })}
                                        />
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                                        <button type="button" onClick={() => setShowIssueModal(false)} className="btn btn-outline">
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={submittingAction}>
                                            {submittingAction ? "Issuing..." : "Issue to Student"}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* ── Bulk Form ── */}
                            {issueMode === "bulk" && (
                                <form onSubmit={handleBulkSubmit}>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem", marginBottom: "1rem" }}>
                                        <div>
                                            <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                                Target Course
                                            </label>
                                            <select
                                                className="form-control"
                                                value={bulkForm.course}
                                                onChange={(e) => setBulkForm({ ...bulkForm, course: e.target.value })}
                                            >
                                                <option value="">All Courses</option>
                                                {courses.map((c) => (
                                                    <option key={c._id} value={c._id}>{c.shortName || c.courseName}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                                Target Year
                                            </label>
                                            <select
                                                className="form-control"
                                                value={bulkForm.yearNumber}
                                                onChange={(e) => setBulkForm({ ...bulkForm, yearNumber: e.target.value })}
                                            >
                                                <option value="">All Years</option>
                                                <option value="1">1st Year</option>
                                                <option value="2">2nd Year</option>
                                                <option value="3">3rd Year</option>
                                                <option value="4">4th Year</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                                Division
                                            </label>
                                            <select
                                                className="form-control"
                                                value={bulkForm.divisionName}
                                                onChange={(e) => setBulkForm({ ...bulkForm, divisionName: e.target.value })}
                                            >
                                                <option value="ALL">All Divisions</option>
                                                <option value="A">Division A</option>
                                                <option value="B">Division B</option>
                                                <option value="C">Division C</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Match count banner */}
                                    <div style={{
                                        padding: "8px 12px",
                                        background: bulkMatchCount > 0 ? "#e8f1f8" : "#fef2f2",
                                        border: `1px solid ${bulkMatchCount > 0 ? "#b6d5ec" : "#fecaca"}`,
                                        borderRadius: "6px",
                                        fontSize: "12.5px",
                                        marginBottom: "1rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                    }}>
                                        <FiUsers size={14} color={bulkMatchCount > 0 ? "#003b67" : "#dc2626"} />
                                        <span>
                                            {checkingBulkCount ? "Counting target students..." : (
                                                <strong>{bulkMatchCount ?? 0} active students</strong>
                                            )} will receive this charge/fine.
                                        </span>
                                    </div>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                        <div>
                                            <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                                Category *
                                            </label>
                                            <select
                                                className="form-control"
                                                value={bulkForm.category}
                                                onChange={(e) => setBulkForm({ ...bulkForm, category: e.target.value })}
                                            >
                                                <option value="Charge">👔 Institutional Charge (Uniform, Lab Manual)</option>
                                                <option value="Fine">⚖ Fine &amp; Penalty (Late Fee, Library)</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                                Type *
                                            </label>
                                            <select
                                                className="form-control"
                                                value={bulkForm.fineType}
                                                onChange={(e) => setBulkForm({ ...bulkForm, fineType: e.target.value })}
                                            >
                                                {FINE_TYPES.map((t) => (
                                                    <option key={t} value={t}>{t}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group" style={{ marginBottom: "1rem" }}>
                                        <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                            Title / Purpose *
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Annual Uniform Kit 2025-26"
                                            className="form-control"
                                            value={bulkForm.title}
                                            onChange={(e) => setBulkForm({ ...bulkForm, title: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                        <div>
                                            <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                                Amount per Student (₹) *
                                            </label>
                                            <input
                                                type="number"
                                                placeholder="e.g. 2300"
                                                className="form-control"
                                                value={bulkForm.amount}
                                                onChange={(e) => setBulkForm({ ...bulkForm, amount: e.target.value })}
                                                min="1"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                                Due Date (Optional)
                                            </label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={bulkForm.dueDate}
                                                onChange={(e) => setBulkForm({ ...bulkForm, dueDate: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                                        <button type="button" onClick={() => setShowIssueModal(false)} className="btn btn-outline">
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={submittingAction || !bulkMatchCount || bulkMatchCount === 0}
                                        >
                                            {submittingAction ? "Processing..." : `Issue to ${bulkMatchCount || 0} Students`}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── WAIVE MODAL ─────────────────────────────────────────────────── */}
            {showWaiveModal && fineToWaive && (
                <div className="modal-overlay" style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
                }}>
                    <div className="card" style={{ width: "100%", maxWidth: "480px" }}>
                        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800 }}>Waive / Excuse {fineToWaive.category === "Charge" ? "Charge" : "Fine"}</h3>
                            <button onClick={() => setShowWaiveModal(false)} className="btn-close" style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}>✕</button>
                        </div>
                        <form onSubmit={handleWaiveSubmit} style={{ padding: "1.25rem" }}>
                            <p style={{ fontSize: "13.5px", color: "var(--text-muted)", marginTop: 0 }}>
                                Waiving <strong style={{ color: "var(--text)" }}>{fineToWaive.title}</strong> for <strong>{fineToWaive.student?.fullName}</strong> in the amount of <strong style={{ color: "#dc2626" }}>{inr(fineToWaive.amount)}</strong>.
                            </p>
                            <div className="form-group" style={{ marginBottom: "1rem" }}>
                                <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                    Justification / Reason for Waiver *
                                </label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="e.g. Principal approved concession due to economic hardship, or equipment found unbroken upon re-inspection"
                                    value={waiverReason}
                                    onChange={(e) => setWaiverReason(e.target.value)}
                                    required
                                />
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                                <button type="button" onClick={() => setShowWaiveModal(false)} className="btn btn-outline">Cancel</button>
                                <button type="submit" className="btn btn-primary" style={{ background: "#7c3aed", borderColor: "#7c3aed" }} disabled={submittingAction}>
                                    {submittingAction ? "Waiving..." : "Confirm Waiver"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── PAY / SETTLE MODAL ─────────────────────────────────────────── */}
            {showPayModal && fineToPay && (
                <div className="modal-overlay" style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
                }}>
                    <div className="card" style={{ width: "100%", maxWidth: "480px" }}>
                        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800 }}>Record Payment &amp; Settle</h3>
                            <button onClick={() => setShowPayModal(false)} className="btn-close" style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}>✕</button>
                        </div>
                        <form onSubmit={handlePaySubmit} style={{ padding: "1.25rem" }}>
                            <div style={{
                                padding: "10px 14px",
                                background: "#e8f1f8",
                                border: "1px solid #b6d5ec",
                                borderRadius: "8px",
                                marginBottom: "1rem",
                                fontSize: "13px",
                            }}>
                                <div>Student: <strong>{fineToPay.student?.fullName}</strong></div>
                                <div>Item: <strong>{fineToPay.title}</strong> ({fineToPay.fineType})</div>
                                <div style={{ fontSize: "15px", fontWeight: 800, color: "#16a34a", marginTop: "4px" }}>
                                    Amount to Collect: {inr(fineToPay.amount)}
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Payment Mode *</label>
                                    <select
                                        className="form-control"
                                        value={payForm.paymentMode}
                                        onChange={(e) => setPayForm({ ...payForm, paymentMode: e.target.value })}
                                    >
                                        <option value="Cash">Cash</option>
                                        <option value="UPI">UPI / QR</option>
                                        <option value="Cheque">Cheque</option>
                                        <option value="NEFT">NEFT / NetBanking</option>
                                        <option value="DD">Demand Draft</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Payment Date *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={payForm.paymentDate}
                                        onChange={(e) => setPayForm({ ...payForm, paymentDate: e.target.value })}
                                        placeholder="DD/MM/YYYY"
                                    />
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1.25rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Voucher / Receipt No</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Auto-generated if blank"
                                        value={payForm.receiptNo}
                                        onChange={(e) => setPayForm({ ...payForm, receiptNo: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Transaction / Ref (UPI/Txn)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Optional transaction ref"
                                        value={payForm.transactionRef}
                                        onChange={(e) => setPayForm({ ...payForm, transactionRef: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                                <button type="button" onClick={() => setShowPayModal(false)} className="btn btn-outline">Cancel</button>
                                <button type="submit" className="btn btn-success" disabled={submittingAction}>
                                    {submittingAction ? "Recording..." : `Confirm Payment of ${inr(fineToPay.amount)}`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
